import React, { useCallback, RefObject, ReactElement } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker as DatePickerInternal } from '@mui/x-date-pickers/DatePicker';
import DatePickerIcon from '../../icons/date-picker-icon';
import DateService from '../../services/date-service';
import useMobileDevice from '../../hooks/use-mobile-device';
import useStylesDatePicker from './date-picker.styles';
import { DATA_REF_NAME } from '../../constants/system/data-attributes';
import { REGEXP_TEMPLATE_ALL_NUMBER_SYMBOLS } from '../../constants/system/regexp-templates';
import { KEYBOARD_KEY_BACKSPACE, KEYBOARD_KEY_NUM_LOCK } from '../../constants/system/keyboard-keys';

const DatePicker: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const MIN_DATE_DEFAULT: string = '1900-01-01';
  const MAX_DATE_DEFAULT: string = '2100-12-31';
  const ERROR_CODE_INVALID_DATE: string = 'invalidDate';
  const ERROR_CODE_MIN_DATE: string = 'minDate';
  const ERROR_CODE_MAX_DATE: string = 'maxDate';
  const ERROR_CODE_DISABLE_FUTURE: string = 'disableFuture';
  const ERROR_CODE_DISABLE_PAST: string = 'disablePast';

  const ERROR_CODE_TITLE_MAP: Record<string, string> = {
    [ERROR_CODE_INVALID_DATE]: 'Invalid Date',
    [ERROR_CODE_MIN_DATE]: 'Invalid Date',
    [ERROR_CODE_MAX_DATE]: 'Invalid Date',
    [ERROR_CODE_DISABLE_FUTURE]: 'Date in the future is not allowed here',
    [ERROR_CODE_DISABLE_PAST]: 'Date in the past is not allowed here',
  };

  const {
    isPlain,
    isReadOnly,
    withBlankValues,
    withNoUnderline,
    withNoPastSelection,
    withNoFutureSelection,
    input,
    value,
    className,
    error,
    errorDeps,
    changeDeps,
    inputRefs,
    inputRefIndex,
    onChange,
    onError,
    ...custom
  } = props;

  const { classes } = useStylesDatePicker();
  const isMobileDevice: boolean = useMobileDevice();

  const inputNoUnderlineClassName: string | undefined = withNoUnderline
    ? classes.datePickerInputNoUnderline : undefined;

  const onErrorInternal: Function = useCallback((errorMessage: number, currentValue: string): void => {
    if (onError) {
      onError(ERROR_CODE_TITLE_MAP[errorMessage] || errorMessage, currentValue);
    }
  }, [withBlankValues, ...(errorDeps || [])]);

  const keyDown: Function = useCallback((event: React.KeyboardEvent): void => {
    if (!value) {
      event.preventDefault();
      return;
    }

    const isNumLockActive: boolean = event.getModifierState(KEYBOARD_KEY_NUM_LOCK as React.ModifierKey);
    const isBackspaceKey: boolean = event.key === KEYBOARD_KEY_BACKSPACE;
    const isNumberKey: boolean = !!(new RegExp(REGEXP_TEMPLATE_ALL_NUMBER_SYMBOLS)).exec(event.key);

    if ((!(isNumLockActive && isNumberKey) && !isBackspaceKey) || event.shiftKey) {
      event.preventDefault();
    }
  }, [value]);

  const onValueChange: Function = useCallback((value: Date): void => {
    if (onChange) {
      onChange((value && DateService.toUtcDate(value)) || null);
    }
  }, [...(changeDeps || [])]);

  return (
    <div className={className}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePickerInternal
          showToolbar={false}
          clearable={withBlankValues}
          disableFuture={withNoFutureSelection}
          disablePast={withNoPastSelection}
          readOnly={isReadOnly}
          inputFormat={DateService.DATE_FORMAT_DEFAULT}
          value={(value && DateService.toUtcDate(value)) || null}
          className={inputNoUnderlineClassName}
          onChange={onValueChange}
          onError={onErrorInternal}
          onKeyDown={keyDown}
          KeyboardButtonProps={{
            [DATA_REF_NAME]: inputRefIndex,
          }}
          inputProps={{
            ref: (inputRef: RefObject<HTMLElement>): null => {
              if (inputRefs) {
                inputRefs.current[inputRefIndex] = inputRef;
              }

              return null;
            },
            [DATA_REF_NAME]: inputRefIndex,
          }}
          slotProps={{
            textField: (inputProps: ReactComponentProps): ReactComponentProps => ({
              error: withBlankValues && !value ? false : (!!error || inputProps.error),
              helperText: error,
              ...(isMobileDevice ? {
                InputProps: {
                  endAdornment: (
                    <DatePickerIcon className={classes.datePickerIcon} />
                  ),
                },
              } : {}),
            }),
          }}
          minDate={new Date(MIN_DATE_DEFAULT)}
          maxDate={new Date(MAX_DATE_DEFAULT)}
          {...(!isPlain ? {
            ...input,
            value: (input && input.value && DateService.toUtcDate(input.value)) || null,
          } : {})}
          {...custom}
        />
      </LocalizationProvider>
    </div>
  );
});

export default DatePicker;
